import React, { createContext, useMemo } from 'react';
import { useTranslations } from '@vp/om-translations';
import { useDocPreviewsGenerator } from '../../hooks/useDocPreviewsGenerator';
import { agentFeedbackToTranslationKeyMap } from '../../constants/agentFeedbackToTranslationKeyMap';
import { AutoResolutionType } from '../../types/feedback/Feedback';
import { OrderDetails } from '../../types/orderDetails';
import LineItemDetails, { ProductAttribute } from '../../types/lineItemDetails';
import {
  FeedbackGroup,
  FeedbackGroupState,
} from '../../types/feedback/FeedbackGroup';

interface IDesignFeedbackRequestGroupContext {
  order: OrderDetails;
  orderId: string;
  groupId: string;
  lineItems: LineItemDetails[];
  lineItemIds: string[];
  totalQuantity: number;
  productName: string;
  productKey: string;
  productVersion: number;
  livePreviewUrl: string;
  documentRefUrl: string;
  productAttributeList: ProductAttribute[];
  orderPlacedAnonymously: boolean;
  emailAddress: string;
  feedbackGroupState: FeedbackGroupState;
  proposedDesignOptions: object;
  originalDesign: object;
  reasons: any[];
  deadline: string;
  isFeedbackPending: boolean;
  isFeedbackProcessing: boolean;
  isRequestLoading: boolean;
  autoResolutionType: AutoResolutionType;
  isPreviewDataError: boolean;
  previewLookupData: { designPreview: object; productPreview: object };
  cimDoc: string;
}

const DesignFeedbackGroupRequestContext =
  createContext<IDesignFeedbackRequestGroupContext | null>(null);

type DesignFeedbackRequestProviderProps = {
  children: React.ReactNode;
  order: OrderDetails;
  orderId: string;
  request: FeedbackGroup;
};

const DesignFeedbackRequestGroupProvider = ({
  children,
  order,
  orderId,
  request,
}: DesignFeedbackRequestProviderProps) => {
  const [designFeedbackReasons] = useTranslations(['designFeedbackReasons']);

  // commonly use data
  const groupId = request.groupId || '';
  const { lineItemIds } = request;
  const lineItemId = request.lineItemIds[0] || '';

  // Line item level data
  const lineItem = useMemo(
    () => order.lineItems?.filter(item => item.id === lineItemId)[0] || {},
    [order, lineItemId]
  );
  const productName = lineItem.productName || '';
  const productKey = lineItem.productKey || '';
  const { productVersion } = lineItem;
  const livePreviewUrl = lineItem.livePreviewUrl || '';
  const documentRefUrl = lineItem.documentUrl || '';
  const productAttributeList = useMemo(
    () => lineItem.displayProductAttributes || [],
    [lineItem.displayProductAttributes]
  );

  const lineItems = useMemo(
    () => order.lineItems?.filter(item => lineItemIds.includes(item.id)) || [],
    [order, lineItemIds]
  );
  const totalQuantity = lineItems.reduce(
    (accumulator, current) => accumulator + current.quantity,
    0
  );

  // commonProductName wil be
  // itemGroup display name if item is in an itemGroup
  // or product name of the item if not
  let commonProductName = lineItem.productName;
  const lineItemGroupId = lineItem.groupInfo?.groupId;
  if (lineItemGroupId) {
    const lineItemGroup = order.itemGroups.filter(
      itemGroup => itemGroup.groupId === lineItemGroupId
    )[0];
    commonProductName = lineItemGroup.groupDisplayName;
  }

  // Order level data
  const orderPlacedAnonymously = order?.orderPlacedAnonymously;
  const emailAddress = order?.emailAddress || '';

  // Feedback group data
  const responseDeadlineDate = request.responseDeadlineDate || '';
  const untranslatedAgentFeedback = request.agentFeedback || [];
  const translatedAgentFeedback = untranslatedAgentFeedback.reduce(
    (arr: any[], untranslatedReason) => {
      const reasonTranslationKey =
        agentFeedbackToTranslationKeyMap[untranslatedReason];
      if (reasonTranslationKey) {
        const reasonTranslation = designFeedbackReasons[reasonTranslationKey];
        arr.push(reasonTranslation);
      }
      return arr || [];
    },
    []
  );
  const autoResolutionType = request.autoResolutionType || '';

  const feedbackGroupState = request.state;
  const isFeedbackPending = true;
  const isFeedbackProcessing = false;

  // Generate a list of preview URLs (one URL for each surface on the doc) for the original and proposed designs
  const {
    proposedDesignOptions,
    originalDesign,
    designsAreReady,
    previewError,
    previewLookupData,
    cimDoc,
  } = useDocPreviewsGenerator(request, lineItem);

  const isRequestLoading = !designsAreReady;
  const isPreviewDataError = previewError;

  const designFeedbackContextValues =
    useMemo<IDesignFeedbackRequestGroupContext>(
      () => ({
        order,
        orderId,
        groupId,
        lineItems,
        lineItemIds,
        totalQuantity,
        commonProductName,
        productName,
        productKey,
        productVersion,
        livePreviewUrl,
        documentRefUrl,
        productAttributeList,
        orderPlacedAnonymously,
        emailAddress,
        feedbackGroupState,
        proposedDesignOptions,
        originalDesign,
        reasons: translatedAgentFeedback,
        deadline: responseDeadlineDate,
        isFeedbackPending,
        isFeedbackProcessing,
        isRequestLoading,
        autoResolutionType,
        isPreviewDataError,
        previewLookupData,
        cimDoc,
      }),
      [
        order,
        orderId,
        groupId,
        lineItems,
        lineItemIds,
        totalQuantity,
        commonProductName,
        productName,
        productKey,
        productVersion,
        livePreviewUrl,
        documentRefUrl,
        productAttributeList,
        orderPlacedAnonymously,
        emailAddress,
        feedbackGroupState,
        proposedDesignOptions,
        originalDesign,
        translatedAgentFeedback,
        responseDeadlineDate,
        isFeedbackPending,
        isFeedbackProcessing,
        isRequestLoading,
        autoResolutionType,
        isPreviewDataError,
        previewLookupData,
        cimDoc,
      ]
    );

  return (
    <DesignFeedbackGroupRequestContext.Provider
      value={designFeedbackContextValues}
    >
      {children}
    </DesignFeedbackGroupRequestContext.Provider>
  );
};

export default DesignFeedbackGroupRequestContext;

export { DesignFeedbackRequestGroupProvider };
